import { seedance25GenerationCredits } from '@/config/credit-economics';
import { VideoResolution } from '@/ai/video-models';
import { Routes } from '@/lib/routes';

/**
 * Trend effects. Each one is a page whose workbench re-films a fixed
 * template clip with the visitor's photos. Add a trend by adding an entry
 * here, uploading its template to R2 at `templateKey`, and adding a route
 * that renders `<TrendWorkbench trend={TRENDS[...]} />`. An effect with
 * `variants` has one template per variant (one R2 key each).
 */
export type TrendId = 'crazy-frog' | 'devil-wears-prada' | 'ramalama';

export interface TrendPhotoSlot {
  id: string;
  label: string;
  hint: string;
  /** How the prompt names the person this photo replaces. */
  role: string;
  /**
   * What the prompt keeps from this photo. Defaults to face, hair, skin tone,
   * body shape, and clothes; trends whose outfit changes inside the clip
   * keep the template's clothes instead.
   */
  keep?: string;
  /** How the prompt names what is in this photo. Defaults to "the person". */
  subject?: string;
}

export interface TrendScene {
  id: string;
  label: string;
  /** Public path of the full image sent to the model. */
  image: string;
  thumbnail: string;
}

export interface TrendSceneOptions {
  label: string;
  hint: string;
  originalLabel: string;
  /** Tile image for "keep the original place", a frame of the template. */
  originalThumbnail: string;
  uploadLabel: string;
  items: TrendScene[];
}

/**
 * One template of a multi-template effect, such as the solo, group, and pet
 * versions of the same walk. A variant replaces the effect's photo slots,
 * template, and preview; everything else (name, path, resolutions) is shared.
 */
export interface TrendVariant {
  id: string;
  /** Short chip label in the workbench, e.g. "Solo" or "Pet". */
  label: string;
  previewVideo: string;
  previewPoster: string;
  photoSlots: TrendPhotoSlot[];
  templateSeconds: number;
  templateKey: string;
  templateDescription: string;
}

export interface TrendVariantOptions {
  /** Legend above the chips, e.g. "Who's walking". */
  label: string;
  defaultId: string;
  items: TrendVariant[];
}

export interface TrendConfig {
  id: TrendId;
  path: string;
  name: string;
  thumbnail: string;
  /** Public, compressed copy of the template shown in the result panel. */
  previewVideo: string;
  previewPoster: string;
  /** One slot per person the clip recasts, in the order the prompt uses. */
  photoSlots: TrendPhotoSlot[];
  /** Background picker; omit for trends that always keep the original place. */
  scenes?: TrendSceneOptions;
  resolutions: VideoResolution[];
  defaultResolution: VideoResolution;
  /** Length of the template clip. The output always matches it. */
  templateSeconds: number;
  /** R2 key of the template clip (MP4, 480p or 720p, 4 to 30 seconds). */
  templateKey: string;
  /** What the template shows, used to build the prompt. */
  templateDescription: string;
  /** Frame of the template; vertical clips use '9:16'. Defaults to '4:3'. */
  frame?: '4:3' | '9:16';
  /**
   * Several templates behind one effect. The top-level template fields mirror
   * the default variant, so code that ignores variants still works.
   */
  variants?: TrendVariantOptions;
}

const CRAZY_FROG_SCENES: TrendScene[] = [
  ['neon-city', 'Neon city'],
  ['desert-canyon', 'Desert canyon'],
  ['snowy-pass', 'Snowy pass'],
  ['beach', 'Beach road'],
  ['supermarket', 'Supermarket'],
  ['mars', 'Mars highway'],
  ['candy-village', 'Candy village'],
].map(([id, label]) => ({
  id,
  label,
  image: `/trends/crazy-frog/scenes/${id}.jpg`,
  thumbnail: `/trends/crazy-frog/scenes/${id}-thumb.jpg`,
}));

const RAMALAMA_KEEP_PEOPLE =
  "Keep each person's face, hair, skin tone, and body shape. Keep the outfits of video 1: everyday jeans and sneakers in the low walk, then the witch costumes after the white camera flash.";

const RAMALAMA_VARIANTS: TrendVariant[] = [
  {
    id: 'solo',
    label: 'Solo',
    previewVideo: '/trends/ramalama/solo-preview.mp4',
    previewPoster: '/trends/ramalama/solo-poster.jpg',
    photoSlots: [
      {
        id: 'photo',
        label: 'Your photo',
        hint: 'Required · one person, face visible, full body or waist-up',
        role: 'the woman who walks toward the camera and then poses in the witch costume',
        keep: RAMALAMA_KEEP_PEOPLE,
      },
    ],
    templateSeconds: 12,
    templateKey: 'trends/ramalama/solo.mp4',
    templateDescription:
      "a camera on the ground films one person from the knees down in jeans and white sneakers walking slowly and heavily toward it down a cobblestone lane lined with glowing jack-o'-lanterns at dusk, a white camera flash fills the frame on the beat, and the same person is revealed full body in a witch costume with a tall pointed hat and a long black cape, staring deadpan, swishing the cape and waving",
  },
  {
    id: 'duo',
    label: '2 people',
    previewVideo: '/trends/ramalama/duo-preview.mp4',
    previewPoster: '/trends/ramalama/duo-poster.jpg',
    photoSlots: [
      {
        id: 'group',
        label: 'Photo of 2 people',
        hint: 'Required · both faces visible, side by side in one photo',
        role: 'the two friends who walk toward the camera and then pose as witches, matched left to right',
        subject: 'the two people',
        keep: RAMALAMA_KEEP_PEOPLE,
      },
    ],
    templateSeconds: 12,
    templateKey: 'trends/ramalama/duo.mp4',
    templateDescription:
      "a camera on the ground films two people side by side from the knees down in jeans and sneakers walking slowly and heavily toward it down a cobblestone lane lined with glowing jack-o'-lanterns at dusk, a white camera flash fills the frame on the beat, and the same two people are revealed full body in witch costumes with tall pointed hats and long capes, staring deadpan, swishing their capes and posing back to back",
  },
  {
    id: 'trio',
    label: '3 people',
    previewVideo: '/trends/ramalama/trio-preview.mp4',
    previewPoster: '/trends/ramalama/trio-poster.jpg',
    photoSlots: [
      {
        id: 'group',
        label: 'Photo of 3 people',
        hint: 'Required · all three faces visible, in a row in one photo',
        role: 'the three friends who walk toward the camera and then pose as witches, matched left to right',
        subject: 'the three people',
        keep: RAMALAMA_KEEP_PEOPLE,
      },
    ],
    templateSeconds: 12,
    templateKey: 'trends/ramalama/trio.mp4',
    templateDescription:
      "a camera on the ground films three people in a row from the knees down in jeans and sneakers walking slowly and heavily toward it down a cobblestone lane lined with glowing jack-o'-lanterns at dusk, a white camera flash fills the frame on the beat, and the same three people are revealed full body in black, deep red, and teal witch costumes with tall pointed hats and capes, staring deadpan, swishing their capes and posing together",
  },
  {
    id: 'pet',
    label: 'Pet',
    previewVideo: '/trends/ramalama/pet-preview.mp4',
    previewPoster: '/trends/ramalama/pet-poster.jpg',
    photoSlots: [
      {
        id: 'pet',
        label: 'Your pet',
        hint: 'Required · one cat or dog, whole body and face in view',
        role: 'the orange tabby cat that walks toward the camera and then sits in the witch hat and cape',
        subject: 'the pet',
        keep: "Keep that pet's species, breed, fur colors, markings, and eye color. Keep the tiny black witch hat and purple cape of video 1 after the white camera flash.",
      },
    ],
    templateSeconds: 12,
    templateKey: 'trends/ramalama/pet.mp4',
    templateDescription:
      "a camera on the ground films an orange tabby cat walking slowly and proudly toward it down a cobblestone lane lined with glowing jack-o'-lanterns at dusk, a white camera flash fills the frame on the beat, and the same cat is revealed sitting in a tiny black witch hat and a purple cape, staring unimpressed, flicking its tail and lifting a paw",
  },
];

const RAMALAMA_DEFAULT = RAMALAMA_VARIANTS[0];

export const TRENDS: Record<TrendId, TrendConfig> = {
  'crazy-frog': {
    id: 'crazy-frog',
    path: Routes.Root,
    name: 'Crazy Frog AI',
    thumbnail: '/trends/crazy-frog/thumb.jpg',
    previewVideo: '/trends/crazy-frog/preview.mp4',
    previewPoster: '/trends/crazy-frog/poster.jpg',
    photoSlots: [
      {
        id: 'lead',
        label: 'Foreground lead',
        hint: 'Required · clear face, waist-up works best (front rider)',
        role: 'the man in the red plaid shirt riding the invisible motorbike in front',
      },
      {
        id: 'chaser',
        label: 'Chaser',
        hint: 'Required · clear face, waist-up works best (red vehicle behind)',
        role: 'the rider in the red cap on the red hover-bike chasing him',
      },
    ],
    scenes: {
      label: 'Background',
      hint: 'The road and camera path stay the same. The place, colors, and light change.',
      originalLabel: 'Original',
      originalThumbnail: '/trends/crazy-frog/thumb.jpg',
      uploadLabel: 'Your own',
      items: CRAZY_FROG_SCENES,
    },
    resolutions: [VideoResolution.P480, VideoResolution.P720],
    defaultResolution: VideoResolution.P480,
    templateSeconds: 11,
    templateKey: 'trends/crazy-frog/template.mp4',
    templateDescription:
      'a man in a red plaid shirt squats in mid-air on an invisible motorbike, fists forward on handlebars that are not there, racing along a futuristic overpass while a rider in a red cap on a red hover-bike chases him',
  },
  'devil-wears-prada': {
    id: 'devil-wears-prada',
    path: Routes.DevilWearsPradaAi,
    name: 'Devil Wears Prada',
    thumbnail: '/trends/devil-wears-prada/thumb.jpg',
    previewVideo: '/trends/devil-wears-prada/preview.mp4',
    previewPoster: '/trends/devil-wears-prada/poster.jpg',
    photoSlots: [
      {
        id: 'selfie',
        label: 'Your selfie',
        hint: 'Required · one person, face to camera, chest-up like a front-camera selfie',
        role: 'the woman who lip-syncs in the at-home selfie and then appears on the night street',
        keep: "Keep that person's face, hair, skin tone, and body shape in both halves. Keep the outfits of video 1: the plain white T-shirt at home, then the oversized black leather jacket on the street.",
      },
    ],
    resolutions: [VideoResolution.P480, VideoResolution.P720],
    defaultResolution: VideoResolution.P480,
    templateSeconds: 12,
    templateKey: 'trends/devil-wears-prada/template.mp4',
    templateDescription:
      'a woman films a front-camera selfie at home in a plain white T-shirt under warm lamp light, lip-syncs and nods to a beat, runs a hand through her hair and makes devil horns with her index fingers, two white panels slide in from the sides and shut, and a hard cut shows her on a windy night street between glass office buildings in an oversized black leather jacket',
  },
  ramalama: {
    id: 'ramalama',
    path: Routes.RamalamaTrend,
    name: 'Ramalama',
    thumbnail: '/trends/ramalama/thumb.jpg',
    previewVideo: RAMALAMA_DEFAULT.previewVideo,
    previewPoster: RAMALAMA_DEFAULT.previewPoster,
    photoSlots: RAMALAMA_DEFAULT.photoSlots,
    resolutions: [VideoResolution.P480, VideoResolution.P720],
    defaultResolution: VideoResolution.P480,
    templateSeconds: RAMALAMA_DEFAULT.templateSeconds,
    templateKey: RAMALAMA_DEFAULT.templateKey,
    templateDescription: RAMALAMA_DEFAULT.templateDescription,
    frame: '9:16',
    variants: {
      label: "Who's walking",
      defaultId: RAMALAMA_DEFAULT.id,
      items: RAMALAMA_VARIANTS,
    },
  },
};

export const TREND_LIST = Object.values(TRENDS);

export function getTrend(id: string): TrendConfig | undefined {
  return TRENDS[id as TrendId];
}

/**
 * The effect as one template: the chosen variant's photo slots, template,
 * and preview merged over the shared settings. Effects without variants
 * come back unchanged. Returns undefined for an unknown variant id.
 */
export function resolveTrendVariant(
  trend: TrendConfig,
  variantId?: string
): TrendConfig | undefined {
  if (!trend.variants) return variantId ? undefined : trend;
  const variant = trend.variants.items.find(
    (item) => item.id === (variantId ?? trend.variants?.defaultId)
  );
  if (!variant) return undefined;
  return {
    ...trend,
    previewVideo: variant.previewVideo,
    previewPoster: variant.previewPoster,
    photoSlots: variant.photoSlots,
    templateSeconds: variant.templateSeconds,
    templateKey: variant.templateKey,
    templateDescription: variant.templateDescription,
  };
}

export function getTrendCredits(
  trend: TrendConfig,
  resolution: VideoResolution
): number {
  return seedance25GenerationCredits({
    resolution,
    seconds: trend.templateSeconds,
  });
}

export type TrendSceneChoice =
  | { kind: 'original' }
  | { kind: 'preset'; sceneId: string }
  | { kind: 'upload' };

/**
 * Prompt for a Seedance 2.5 edit. Images are numbered in upload order:
 * one per photo slot, then the background (preset or upload) last.
 */
export function buildTrendPrompt(
  trend: TrendConfig,
  scene: TrendSceneChoice
): string {
  const lines = [
    `Edit video 1, in which ${trend.templateDescription}.`,
    'Keep the exact motion, poses, camera moves, cuts, and timing of video 1.',
  ];
  trend.photoSlots.forEach((slot, index) => {
    lines.push(
      `Replace ${slot.role} with ${slot.subject ?? 'the person'} in image ${index + 1}. ${
        slot.keep ??
        "Keep that person's face, hair, skin tone, body shape, and clothes."
      }`
    );
  });
  if (scene.kind === 'original') {
    lines.push('Keep the original location, lighting, and colors of video 1.');
  } else {
    lines.push(
      `Move the scene to the place shown in image ${trend.photoSlots.length + 1}: use its setting, colors, materials, and light, while keeping the road layout and camera path of video 1.`
    );
  }
  lines.push('No text, no logos, no watermarks, no extra people.');
  return lines.join(' ');
}
